/**
 * Iter331 · Dashboard card — Taxonomy Quality.
 * Shows how many CRM leads are "Unclassified" and offers an AI-driven
 * classifier that suggests segments via Claude Sonnet 5. Suggestions are
 * approved one-by-one before being applied.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Sparkles, Loader2, Check, X, ChevronRight, Zap, StopCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TaxonomyQualityCard() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [engine, setEngine] = useState("");
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [applying, setApplying] = useState(false);
  const [approved, setApproved] = useState({}); // {lead_id: segment}
  // Iter332 · batch job state
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchJob, setBatchJob] = useState(null);
  const [batchStarting, setBatchStarting] = useState(false);
  const [batchParams, setBatchParams] = useState({
    max_leads: 500, min_confidence: 0.75, auto_apply: true, chunk_size: 15,
  });
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoadingOverview(true);
    try {
      const { data } = await axios.get(`${API}/leads-finder/quality/overview`);
      setOverview(data);
    } catch { /* silent — card just stays empty */ }
    finally { setLoadingOverview(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const suggest = async () => {
    setLoadingSuggest(true);
    setSuggestions([]); setApproved({});
    try {
      const { data } = await axios.post(
        `${API}/leads-finder/quality/suggest`, { limit: 15 });
      setSuggestions(data.suggestions || []);
      setEngine(data.engine || "");
      // Auto-approve entries with confidence >= 0.75.
      const map = {};
      (data.suggestions || []).forEach((s) => {
        if (s.confidence >= 0.75 && s.suggestion) map[s.lead_id] = s.suggestion;
      });
      setApproved(map);
      toast.success(isPt
        ? `${data.count} sugestões · motor: ${data.engine}`
        : `${data.count} suggestions · engine: ${data.engine}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suggest failed");
    } finally { setLoadingSuggest(false); }
  };

  const apply = async () => {
    const applies = Object.entries(approved)
      .map(([lead_id, segment]) => ({ lead_id, segment }))
      .filter((a) => a.segment);
    if (!applies.length) {
      toast.info(isPt ? "Aprove pelo menos uma sugestão" : "Approve at least one");
      return;
    }
    setApplying(true);
    try {
      const { data } = await axios.post(
        `${API}/leads-finder/quality/apply`, { applies });
      toast.success(isPt
        ? `${data.updated} leads reclassificados`
        : `${data.updated} leads reclassified`);
      setSuggestions([]); setApproved({});
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setApplying(false); }
  };

  // Iter332 · Batch job — start / poll / cancel
  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };
  useEffect(() => () => stopPoll(), []);

  const pollJob = useCallback((jobId) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(
          `${API}/leads-finder/quality/batch/status/${jobId}`);
        setBatchJob(data);
        if (["done", "failed", "cancelled"].includes(data.status)) {
          stopPoll();
          toast.success(isPt
            ? `Lote ${data.status}: ${data.counters.applied} aplicados`
            : `Batch ${data.status}: ${data.counters.applied} applied`);
          await load();
        }
      } catch (e) {
        stopPoll();
        toast.error("Polling failed");
      }
    }, 3000);
  }, [isPt, load]);

  const startBatch = async () => {
    setBatchStarting(true);
    try {
      const { data } = await axios.post(
        `${API}/leads-finder/quality/batch/start`, batchParams);
      if (data.already_running) {
        setBatchJob(data.job);
        toast.info(isPt ? "Um lote já está rodando" : "A batch is already running");
        pollJob(data.job.id);
      } else {
        setBatchJob(data.job);
        toast.success(isPt ? "Lote iniciado" : "Batch started");
        pollJob(data.job_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch start failed");
    } finally { setBatchStarting(false); }
  };

  const cancelBatch = async () => {
    if (!batchJob?.id) return;
    try {
      await axios.post(`${API}/leads-finder/quality/batch/${batchJob.id}/cancel`);
      toast.info(isPt ? "Cancelando..." : "Cancelling...");
    } catch { toast.error("Cancel failed"); }
  };

  if (loadingOverview) {
    return (
      <div className="al-card p-4 animate-pulse" data-testid="tax-quality-card">
        <div className="h-3 w-40 bg-[#E5E7EB] rounded mb-2" />
        <div className="h-6 w-20 bg-[#E5E7EB] rounded" />
      </div>
    );
  }
  if (!overview || overview.total === 0) return null;

  const pct = overview.clean_pct;
  const warn = overview.unclassified > 0;
  return (
    <section className="al-card p-5" data-testid="tax-quality-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${
              warn ? "bg-amber-500/15" : "bg-emerald-500/15"}`}>
              {warn
                ? <AlertTriangle className="w-4 h-4 text-amber-500" />
                : <Check className="w-4 h-4 text-emerald-600" />}
            </span>
            <h3 className="text-[14px] font-bold">
              {isPt ? "Qualidade da Taxonomia" : "Taxonomy Quality"}
            </h3>
          </div>
          <p className="text-[12px] text-[var(--al-text-muted)]">
            {isPt
              ? `${overview.unclassified} de ${overview.total} leads sem segmento válido`
              : `${overview.unclassified} of ${overview.total} leads without a valid segment`}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[28px] font-extrabold tabular-nums"
               data-testid="tax-quality-pct"
               style={{ color: warn ? "#F59E0B" : "#10B981" }}>
            {pct}%
          </div>
          <div className="text-[10.5px] uppercase font-bold text-[var(--al-text-muted)] tracking-wider">
            {isPt ? "limpo" : "clean"}
          </div>
        </div>
      </div>

      <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden mt-3">
        <div className="h-full transition-all"
             style={{ width: `${pct}%`,
                      background: warn ? "#F59E0B" : "#10B981" }} />
      </div>

      {warn && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={suggest} disabled={loadingSuggest}
            data-testid="tax-quality-suggest-btn"
            className="h-9 px-3 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
            {loadingSuggest
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Sparkles className="w-3.5 h-3.5" />}
            {isPt ? "Sugerir 15 (IA)" : "Suggest 15 (AI)"}
          </button>
          <button type="button" onClick={() => setBatchOpen((v) => !v)}
            data-testid="tax-quality-batch-toggle"
            className="h-9 px-3 rounded-lg bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233] text-[12px] font-bold inline-flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" />
            {isPt ? "Auto Reclass em Massa" : "Auto Reclass Batch"}
          </button>
          <button type="button" onClick={() => navigate("/leads-finder")}
            data-testid="tax-quality-open-leads"
            className="h-9 px-3 rounded-lg border border-[var(--al-border)] text-[11.5px] font-semibold inline-flex items-center gap-1 hover:bg-[var(--al-bg)]">
            {isPt ? "Abrir Leads Finder" : "Open Leads Finder"}
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Iter332 · Batch panel */}
      {batchOpen && (
        <div className="mt-4 border-t border-[var(--al-border)] pt-3"
             data-testid="tax-quality-batch-panel">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11.5px] font-bold uppercase tracking-wider text-[var(--al-text-muted)]">
              {isPt ? "Reclassificação em massa" : "Batch reclassification"}
            </span>
            {batchJob && ["queued", "running"].includes(batchJob.status) && (
              <button type="button" onClick={cancelBatch}
                data-testid="tax-quality-batch-cancel"
                className="h-7 px-2 rounded bg-red-500/10 text-red-500 text-[11px] font-bold inline-flex items-center gap-1 hover:bg-red-500 hover:text-white">
                <StopCircle className="w-3 h-3" /> {isPt ? "Cancelar" : "Cancel"}
              </button>
            )}
          </div>

          {!batchJob || ["done", "failed", "cancelled"].includes(batchJob?.status) ? (
            <div className="space-y-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <label className="block text-[10.5px]">
                  <span className="uppercase font-bold text-[var(--al-text-muted)]">
                    {isPt ? "Máx leads" : "Max leads"}
                  </span>
                  <input type="number" min={10} max={20000}
                    value={batchParams.max_leads}
                    onChange={(e) => setBatchParams((p) => ({ ...p, max_leads: Number(e.target.value) }))}
                    data-testid="tax-batch-maxleads"
                    className="al-input h-8 w-full text-[12px] mt-1" />
                </label>
                <label className="block text-[10.5px]">
                  <span className="uppercase font-bold text-[var(--al-text-muted)]">
                    {isPt ? "Confiança mín" : "Min conf"}
                  </span>
                  <select value={batchParams.min_confidence}
                    onChange={(e) => setBatchParams((p) => ({ ...p, min_confidence: Number(e.target.value) }))}
                    data-testid="tax-batch-minconf"
                    className="al-input h-8 w-full text-[12px] mt-1">
                    <option value={0.5}>50%</option>
                    <option value={0.6}>60%</option>
                    <option value={0.7}>70%</option>
                    <option value={0.75}>75%</option>
                    <option value={0.85}>85%</option>
                    <option value={0.9}>90%</option>
                  </select>
                </label>
                <label className="block text-[10.5px]">
                  <span className="uppercase font-bold text-[var(--al-text-muted)]">
                    {isPt ? "Chunk" : "Chunk"}
                  </span>
                  <select value={batchParams.chunk_size}
                    onChange={(e) => setBatchParams((p) => ({ ...p, chunk_size: Number(e.target.value) }))}
                    data-testid="tax-batch-chunk"
                    className="al-input h-8 w-full text-[12px] mt-1">
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                  </select>
                </label>
                <label className="block text-[10.5px]">
                  <span className="uppercase font-bold text-[var(--al-text-muted)]">
                    {isPt ? "Aplicar" : "Apply"}
                  </span>
                  <select value={batchParams.auto_apply ? "yes" : "no"}
                    onChange={(e) => setBatchParams((p) => ({ ...p, auto_apply: e.target.value === "yes" }))}
                    data-testid="tax-batch-apply"
                    className="al-input h-8 w-full text-[12px] mt-1">
                    <option value="yes">{isPt ? "Auto" : "Auto"}</option>
                    <option value="no">{isPt ? "Só relatório" : "Report only"}</option>
                  </select>
                </label>
              </div>
              <button type="button" onClick={startBatch} disabled={batchStarting}
                data-testid="tax-quality-batch-start"
                className="h-9 px-4 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
                {batchStarting
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Zap className="w-3.5 h-3.5" />}
                {isPt
                  ? `Rodar em ${batchParams.max_leads} leads`
                  : `Run on ${batchParams.max_leads} leads`}
              </button>
              {batchJob && ["done", "failed", "cancelled"].includes(batchJob.status) && (
                <BatchReport job={batchJob} isPt={isPt} />
              )}
            </div>
          ) : (
            <BatchProgress job={batchJob} isPt={isPt} />
          )}
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="mt-4 border-t border-[var(--al-border)] pt-3"
             data-testid="tax-quality-suggestions">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11.5px] font-bold uppercase tracking-wider text-[var(--al-text-muted)]">
              {isPt ? "Sugestões" : "Suggestions"} · {engine}
            </span>
            <button type="button" onClick={apply}
              disabled={applying || Object.keys(approved).length === 0}
              data-testid="tax-quality-apply-btn"
              className="h-8 px-3 rounded-lg bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233] text-[11.5px] font-bold inline-flex items-center gap-1 disabled:opacity-50">
              {applying
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Check className="w-3.5 h-3.5" />}
              {isPt
                ? `Aplicar ${Object.keys(approved).length}`
                : `Apply ${Object.keys(approved).length}`}
            </button>
          </div>
          <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
            {suggestions.map((s) => {
              const approvedSeg = approved[s.lead_id];
              return (
                <div key={s.lead_id}
                     data-testid={`tax-suggest-row-${s.lead_id}`}
                     className="flex items-center gap-2 text-[11.5px] px-2 py-1.5 rounded hover:bg-[var(--al-bg)]">
                  <button type="button"
                    onClick={() => setApproved((prev) => {
                      const n = { ...prev };
                      if (n[s.lead_id]) delete n[s.lead_id];
                      else n[s.lead_id] = s.suggestion;
                      return n;
                    })}
                    data-testid={`tax-suggest-approve-${s.lead_id}`}
                    className={`w-5 h-5 rounded border flex items-center justify-center ${
                      approvedSeg
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : "border-[var(--al-border)]"}`}>
                    {approvedSeg ? <Check className="w-3 h-3" /> : null}
                  </button>
                  <span className="flex-1 truncate">
                    <span className="font-semibold">{s.lead_id.slice(0, 8)}</span>
                    <span className="mx-1 text-[var(--al-text-muted)]">→</span>
                    <span className="font-bold text-[#1B3A8A]">
                      {s.suggestion || "—"}
                    </span>
                  </span>
                  <span className="text-[10px] tabular-nums text-[var(--al-text-muted)]"
                        title={s.reason}>
                    {(s.confidence * 100).toFixed(0)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

// ── Batch progress + report helpers ──────────────────────────────────

function BatchProgress({ job, isPt }) {
  const c = job.counters || {};
  const total = c.candidates || 0;
  const done = c.processed || 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="space-y-2" data-testid="tax-batch-progress">
      <div className="flex items-center gap-2 text-[12px]">
        <Loader2 className="w-4 h-4 animate-spin text-[#C9A24E]" />
        <span className="font-semibold">
          {done}/{total} {isPt ? "processados" : "processed"} · {pct}%
        </span>
        <span className="text-[10.5px] text-[var(--al-text-muted)]">
          {isPt ? "aplicados" : "applied"}: <b>{c.applied || 0}</b> ·
          {isPt ? " alta conf" : " high conf"}: <b>{c.high_conf || 0}</b>
        </span>
      </div>
      <div className="w-full h-1.5 bg-[var(--al-border)] rounded overflow-hidden">
        <div className="h-full bg-[#C9A24E] transition-all"
             style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function BatchReport({ job, isPt }) {
  const c = job.counters || {};
  const bins = job.confidence_bins || {};
  const total = c.processed || 0;
  return (
    <div className="mt-3 space-y-2 border border-[var(--al-border)] rounded-lg p-3"
         data-testid="tax-batch-report">
      <div className="text-[11.5px] font-bold uppercase tracking-wider text-[var(--al-text-muted)]">
        {isPt ? `Relatório · ${job.status}` : `Report · ${job.status}`}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
        <Stat label={isPt ? "Processados" : "Processed"} value={total} />
        <Stat label={isPt ? "Aplicados" : "Applied"} value={c.applied || 0}
              color="#10B981" />
        <Stat label={isPt ? "Alta conf" : "High conf"}
              value={c.high_conf || 0} color="#1B3A8A" />
        <Stat label={isPt ? "Erros" : "Errors"} value={c.errors || 0}
              color="#EF4444" />
      </div>
      <div className="text-[10.5px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] pt-1">
        {isPt ? "Distribuição de confiança" : "Confidence distribution"}
      </div>
      <div className="flex items-center gap-1">
        {[
          ["90+", "#10B981"],
          ["75-89", "#1B3A8A"],
          ["50-74", "#F59E0B"],
          ["<50", "#EF4444"],
        ].map(([bin, color]) => {
          const v = bins[bin] || 0;
          const w = total ? Math.round((v / total) * 100) : 0;
          return (
            <div key={bin} className="flex-1" title={`${bin}: ${v}`}>
              <div className="text-[9.5px] text-center text-[var(--al-text-muted)] mb-0.5">
                {bin}: {v}
              </div>
              <div className="h-1.5 rounded"
                   style={{ background: color, opacity: v ? 1 : 0.15,
                            width: `${Math.max(w, 4)}%`, minWidth: 12 }} />
            </div>
          );
        })}
      </div>
      {job.engine_stats && (
        <div className="text-[10.5px] text-[var(--al-text-muted)]">
          {isPt ? "Motor" : "Engine"}:
          {" "}Claude {job.engine_stats["claude-sonnet-5"] || 0} · Heurística {job.engine_stats.heuristic || 0}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color = "#111827" }) {
  return (
    <div className="al-card px-2 py-1.5 flex items-center justify-between">
      <span className="uppercase font-bold text-[9.5px] tracking-wider text-[var(--al-text-muted)]">
        {label}
      </span>
      <span className="font-extrabold tabular-nums" style={{ color }}>
        {value}
      </span>
    </div>
  );
}
